import { useEffect, useMemo } from "react";
import { sitePhrasing } from "../../../../Language/languageHandler";
import { AddressInputFieldGroup } from "../../../FormFields/AddressFieldGroup/AddressInputFieldGroup";
import { ArrayEntryFieldType, ArrayFieldInput } from "../../../FormFields/ArrayField/ArrayFieldInput";
import CheckboxInputField from "../../../FormFields/CheckboxInputField/CheckboxInputField";
import RadioInputField from "../../../FormFields/RadioInputField/RadioInputField";
import SelectFieldInput from "../../../FormFields/SelectInputField/SelectInputField";
import TextInputField from "../../../FormFields/TextInputField/TextInputField";
import { annualReportDisclaimer1, annualReportTypeOfBusiness, electronicSignatureSublabelGeneral, iAgreeText, laborLawPosterAgreementText, mayBeContactedAgreementText, standardAddressFields, stateTransactionAcknowledgmentText, yesOrNoValues } from "../PurchaseConstants";
import { forcedRegistered } from "../PurchaseFormHelpers";
import { AnnualReferenceNumberInstructions, TermsAndConditionAcceptText } from "../SharedPurchaseFieldComponents";
import { poBoxPattern } from "./StateSpecificAnnualReportFields";
import { ContactInfoFields } from "../ContactInfoFields";
import { MontanaAnnualReportTerms } from "./StateSpecificAnnualTerms";

export default function MontanaAnnualReportFields(props: {
    errors: any;
    touched: any;
    values: any;
    setFieldValue: (field: string, value: any) => void;
    selectedProducts: any;
}) {
    const {
        errors,
        touched,
        values,
        setFieldValue,
        selectedProducts = {},
    } = props;

    const mailingAddress = values.annualReport?.informationChange?.mailingAddress?.newAddress?.address || '';
    const isMailingPOBox = useMemo(() => poBoxPattern.test(mailingAddress), [mailingAddress]);

    useEffect(() => {
        if (isMailingPOBox && values.annualReport.informationChange.principalAddress.changed !== 'yes') {
            setFieldValue('annualReport.informationChange.principalAddress.changed', 'yes');
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isMailingPOBox]
    );

    return (
        <>

            <ContactInfoFields
                values={values}
                touched={touched}
                errors={errors}
                setFieldValue={setFieldValue}
            />

            <TextInputField
                mask={sitePhrasing.referenceMask}
                fieldName={"referenceNumber"}
                fieldLabel={"Reference Number"}
                fieldSubLabel={undefined}
                customFieldInstructions={() => (<AnnualReferenceNumberInstructions />)}
                errors={errors}
                touched={touched}
                required={true}
                placeholder={"Reference Number"}
                maskGuide={sitePhrasing.referenceMaskGuide}
            />

            <TextInputField
                fieldName={"businessName"}
                fieldLabel={"Complete Business Name"}
                errors={errors}
                touched={touched}
                required={true}
                placeholder={"Complete Business Name"}
            />


            <RadioInputField
                fieldName={"annualReport.typeOfEntity"}
                fieldLabel={"What type of entity is your company?"}
                errors={errors}
                required={true}
                touched={touched}
                options={annualReportTypeOfBusiness}
            />
            {values.annualReport.typeOfEntity === "Corp, INC, other" && (
                <>
                    <TextInputField
                        fieldName={"annualReport.natureOfBusiness"}
                        fieldLabel={"Business Purpose?"}
                        errors={errors}
                        touched={touched}
                        required={true}
                        placeholder={"Describe the business being conducted."}
                    />
                </>
            )}


            <b>Business Mailing Address of Principal Office*</b><br />
            <i>Update the business mailing address if changed</i>
            <br /><br />
            <AddressInputFieldGroup
                fieldName={
                    "annualReport.informationChange.mailingAddress.newAddress"
                }
                errors={errors}
                touched={touched}
                fieldLabels={{
                    address: 'Mailing Address 1',
                    address2: 'Mailing Address 2',
                }}
            />

            <RadioInputField
                fieldName={"annualReport.informationChange.principalAddress.changed"}
                fieldLabel={"Business Physical Address of Principal Office?"}
                errors={errors}
                required={true}
                touched={touched}
                fieldSubLabel="No PO Boxes"
                disabled={isMailingPOBox}
                customClass="full-width error-sub-label bold-sub-label"
                options={isMailingPOBox ? [] : [
                    { value: 'no', label: 'Same as mailing address above' },
                    { value: 'yes', label: 'Different address' },
                ]}
            />
            {/* {isMailingPOBox && (
                <div className="field-note">
                    * Your mailing address is a PO Box. A physical street address is required.
                </div>
            )} */}
            {values.annualReport.informationChange.principalAddress.changed === "yes" && (
                <AddressInputFieldGroup
                    fieldName={
                        "annualReport.informationChange.principalAddress.newAddress"
                    }
                    errors={errors}
                    touched={touched}
                // fieldMessages={{
                //   state: forcedStatePrincipal[sitePhrasing.state] ? `Your principal address must be located in ${sitePhrasing.state}.` : ``
                // }}
                // disableFields={{
                //   state: forcedStatePrincipal[sitePhrasing.state] ? true : false,
                // }}
                />
            )}

            <h3>Do you need to change any of the following information?</h3>



            <RadioInputField
                fieldName={"annualReport.informationChange.registeredAgent.agentChange"}
                fieldLabel={"Registered Agent?"}
                errors={errors}
                required={true}
                touched={touched}
                options={yesOrNoValues}
            />
            {values.annualReport.informationChange.registeredAgent.agentChange === "yes" && (
                <>
                    <RadioInputField
                        fieldName={"annualReport.informationChange.registeredAgent.agentChangeType"}
                        fieldLabel={"What type of change?"}
                        errors={errors}
                        required={true}
                        touched={touched}
                        customClass={`full-width`}
                        options={[
                            { value: "address", label: "Change my current Registered Agent's address only", },
                            { value: "nameAddress", label: "Change my Registered Agent's name and/or address", },
                        ]}
                    />

                    {values.annualReport.informationChange.registeredAgent.agentChangeType === "address" && (
                        <>
                            <AddressInputFieldGroup
                                fieldName={"annualReport.informationChange.registeredAgent.newStreetAddress"}
                                errors={errors}
                                touched={touched}
                                fieldMessages={{
                                    state: forcedRegistered[sitePhrasing.state] ? `Your registered agent must be located in ${sitePhrasing.state}.` : ``,
                                }}
                                disableFields={{
                                    state: forcedRegistered[sitePhrasing.state] ? true : false,
                                }}
                            />

                            <RadioInputField
                                fieldName={"annualReport.informationChange.registeredAgent.agentMailingAddressChange"}
                                fieldLabel={"Registered Mailing Address?"}
                                errors={errors}
                                required={true}
                                touched={touched}
                                customClass="full-width"
                                options={[
                                    { value: 'no', label: 'Same as registered agent address above' },
                                    { value: 'yes', label: 'Different address' },
                                ]}
                            />
                            {values.annualReport.informationChange.registeredAgent.agentMailingAddressChange === "yes" && (
                                <AddressInputFieldGroup
                                    fieldName={
                                        "annualReport.informationChange.registeredAgent.newAddress"
                                    }
                                    errors={errors}
                                    touched={touched}
                                    fieldMessages={{
                                        state: forcedRegistered[sitePhrasing.state] ? `Your registered agent must be located in ${sitePhrasing.state}.` : ``,
                                    }}
                                    disableFields={{
                                        state: forcedRegistered[sitePhrasing.state] ? true : false,
                                    }}
                                    fieldLabels={{
                                        address: 'Mailing Address 1',
                                        address2: 'Mailing Address 2',
                                    }}
                                />
                            )}

                        </>
                    )}

                    {values.annualReport.informationChange.registeredAgent.agentChangeType === "nameAddress" && (
                        <>
                            <RadioInputField
                                fieldName={"annualReport.informationChange.registeredAgent.agentType"}
                                fieldLabel={"My Registered Agent is"}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={[
                                    { value: "individual", label: "Individual" },
                                    { value: "organization", label: "Organization" },
                                ]}
                            />



                            {values.annualReport.informationChange.registeredAgent.agentType === "individual" && (
                                <>
                                    <TextInputField
                                        fieldName={"annualReport.informationChange.registeredAgent.firstName"}
                                        fieldLabel={"First Name"}
                                        errors={errors}
                                        touched={touched}
                                        required={true}
                                        placeholder={"First Name"}
                                    />
                                    <TextInputField
                                        fieldName={"annualReport.informationChange.registeredAgent.lastName"}
                                        fieldLabel={"Last Name"}
                                        errors={errors}
                                        touched={touched}
                                        required={true}
                                        placeholder={"Last Name"}
                                    />
                                </>
                            )}

                            {values.annualReport.informationChange.registeredAgent.agentType === "organization" && (
                                <>
                                    <TextInputField
                                        fieldName={"annualReport.informationChange.registeredAgent.businessName"}
                                        fieldLabel={"Organization"}
                                        errors={errors}
                                        touched={touched}
                                        required={true}
                                        placeholder={"Business Name"}
                                    />
                                    <div className="field-note">
                                        * Business must be active and registered in the state of
                                        Montana. Please type business name exactly as it appears on the
                                        Montana Secretary of State to avoid errors and delays in your
                                        order. It is possible we may contact you if we are unable to locate the business.
                                    </div>
                                </>
                            )}

                            {(values.annualReport.informationChange.registeredAgent.agentType === "organization" || values.annualReport.informationChange.registeredAgent.agentType === "individual")
                                &&
                                (
                                    <>
                                        <TextInputField
                                            fieldName={"annualReport.informationChange.registeredAgent.email"}
                                            fieldLabel={"Email"}
                                            errors={errors}
                                            touched={touched}
                                            required={true}
                                            placeholder={"Email"}
                                        />

                                        <AddressInputFieldGroup
                                            fieldName={"annualReport.informationChange.registeredAgent.newStreetAddress"}
                                            errors={errors}
                                            touched={touched}
                                            fieldMessages={{
                                                state: forcedRegistered[sitePhrasing.state] ? `Your registered agent must be located in ${sitePhrasing.state}.` : ``,
                                            }}
                                            disableFields={{
                                                state: forcedRegistered[sitePhrasing.state] ? true : false,
                                            }}
                                        />


                                        <RadioInputField
                                            fieldName={"annualReport.informationChange.registeredAgent.agentMailingAddressChange"}
                                            fieldLabel={"Registered Mailing Address?"}
                                            errors={errors}
                                            required={true}
                                            touched={touched}
                                            customClass="full-width"
                                            options={[
                                                { value: 'no', label: 'Same as registered agent address above' },
                                                { value: 'yes', label: 'Different address' },
                                            ]}
                                        />
                                        {values.annualReport.informationChange.registeredAgent.agentMailingAddressChange === "yes" && (
                                            <AddressInputFieldGroup
                                                fieldName={
                                                    "annualReport.informationChange.registeredAgent.newAddress"
                                                }
                                                errors={errors}
                                                touched={touched}
                                                fieldMessages={{
                                                    state: forcedRegistered[sitePhrasing.state] ? `Your registered agent must be located in ${sitePhrasing.state}.` : ``,
                                                }}
                                                disableFields={{
                                                    state: forcedRegistered[sitePhrasing.state] ? true : false,
                                                }}
                                                fieldLabels={{
                                                    address: 'Mailing Address 1',
                                                    address2: 'Mailing Address 2',
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                        </>
                    )}
                </>

            )}

            {values.annualReport.typeOfEntity === 'LLC' && (
                <>
                    <RadioInputField
                        fieldName={"annualReport.informationChange.llcManager.llcManagerChange"}
                        fieldLabel={"Manager(s)/Member(s)?"}
                        fieldSubLabel={"You must maintain a minimum of 1 person on file"}
                        errors={errors}
                        required={true}
                        touched={touched}
                        options={yesOrNoValues}
                        customClass={"error-sub-label italic-sub-label"}
                    />
                    {values.annualReport.informationChange.llcManager.llcManagerChange === "yes" && (
                        <>
                            <RadioInputField
                                fieldName={"annualReport.informationChange.llcManager.removingAgents"}
                                fieldLabel={`Are you removing any manager(s)/member(s)?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />
                            {values.annualReport.informationChange.llcManager.removingAgents === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.llcManager.removedAgents"}
                                    values={values.annualReport.informationChange.llcManager.removedAgents}
                                    blankEntryFn={() => ({
                                        name: "",
                                    })}
                                    fields={[
                                        {
                                            fieldName: "name",
                                            fieldLabel: "Name",
                                            placeholder: "Name",
                                            fieldType: "text",
                                            required: true,
                                        }
                                    ]}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}

                            <RadioInputField
                                fieldName={"annualReport.informationChange.llcManager.addingAgents"}
                                fieldLabel={`Are you adding any manager(s)/member(s)?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />
                            {values.annualReport.informationChange.llcManager.addingAgents === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.llcManager.addedAgents"}
                                    values={values.annualReport.informationChange.llcManager.addedAgents}
                                    blankEntryFn={() => ({
                                        entityType: "",
                                        firstName: "",
                                        lastName: "",
                                        name: "",
                                        address: "",
                                        address2: "",
                                        city: "",
                                        state: "",
                                        zip: "",
                                    })}
                                    fields={MontanaLLCAgentFields}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}


                            <RadioInputField
                                fieldName={"annualReport.informationChange.llcManager.changingAddresses"}
                                fieldLabel={`Change address for existing manager(s)/member(s)?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />

                            {values.annualReport.informationChange.llcManager.changingAddresses === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.llcManager.changedAddresses"}
                                    values={values.annualReport.informationChange.llcManager.changedAddresses}
                                    blankEntryFn={() => ({
                                        name: "",
                                        address: "",
                                        address2: "",
                                        city: "",
                                        state: "",
                                        zip: "",
                                    })}
                                    fields={MontanaLLCAgentAddressFields}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}

                        </>
                    )}
                </>
            )}


            {/* DIRECTOR CHANGES */}
            {values.annualReport.typeOfEntity === 'Corp, INC, other' && (
                <>
                    <RadioInputField
                        fieldName={"annualReport.informationChange.director.directorChange"}
                        fieldLabel={"Directors?"}
                        // fieldSubLabel={"You must maintain a minimum of 1 person on file"}
                        errors={errors}
                        required={true}
                        touched={touched}
                        options={yesOrNoValues}
                    // customClass={"error-sub-label italic-sub-label"}
                    />
                    {values.annualReport.informationChange.director.directorChange === "yes" && (
                        <>
                            <RadioInputField
                                fieldName={"annualReport.informationChange.director.removingDirectors"}
                                fieldLabel={`Are you removing any Directors?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />
                            {values.annualReport.informationChange.director.removingDirectors === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.director.removedDirectors"}
                                    values={values.annualReport.informationChange.director.removedDirectors}
                                    blankEntryFn={() => ({
                                        name: "",
                                    })}
                                    fields={[
                                        {
                                            fieldName: "name",
                                            fieldLabel: "Name",
                                            placeholder: "Name",
                                            fieldType: "text",
                                            required: true,
                                        }
                                    ]}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}

                            <RadioInputField
                                fieldName={"annualReport.informationChange.director.addingDirectors"}
                                fieldLabel={`Are you adding any Directors?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />
                            {values.annualReport.informationChange.director.addingDirectors === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.director.addedDirectors"}
                                    values={values.annualReport.informationChange.director.addedDirectors}
                                    blankEntryFn={() => ({
                                        firstName: "",
                                        lastName: "",
                                        address: "",
                                        address2: "",
                                        city: "",
                                        state: "",
                                        zip: "",
                                    })}
                                    fields={MontanaAgentAddressFields}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}


                            <RadioInputField
                                fieldName={"annualReport.informationChange.director.changingAddresses"}
                                fieldLabel={`Change address for existing Directors?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />

                            {values.annualReport.informationChange.director.changingAddresses === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.director.changedAddresses"}
                                    values={values.annualReport.informationChange.director.changedAddresses}
                                    blankEntryFn={() => ({
                                        firstName: "",
                                        lastName: "",
                                        address: "",
                                        address2: "",
                                        city: "",
                                        state: "",
                                        zip: "",
                                    })}
                                    fields={MontanaAgentAddressFields}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}

                        </>
                    )}
                </>
            )}

            {/* OFFICER CHANGES */}
            {values.annualReport.typeOfEntity === 'Corp, INC, other' && (
                <>
                    <RadioInputField
                        fieldName={"annualReport.informationChange.officer.officerChange"}
                        fieldLabel={"Officers?"}
                        // fieldSubLabel={"You must maintain a minimum of 1 person on file"}
                        errors={errors}
                        required={true}
                        touched={touched}
                        options={yesOrNoValues}
                    // customClass={"error-sub-label italic-sub-label"}
                    />
                    {values.annualReport.informationChange.officer.officerChange === "yes" && (
                        <>
                            <RadioInputField
                                fieldName={"annualReport.informationChange.officer.removingOfficers"}
                                fieldLabel={`Are you removing any Officers?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />
                            {values.annualReport.informationChange.officer.removingOfficers === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.officer.removedOfficers"}
                                    values={values.annualReport.informationChange.officer.removedOfficers}
                                    blankEntryFn={() => ({
                                        name: "",
                                    })}
                                    fields={[
                                        {
                                            fieldName: "name",
                                            fieldLabel: "Name",
                                            placeholder: "Name",
                                            fieldType: "text",
                                            required: true,
                                        }
                                    ]}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}

                            <RadioInputField
                                fieldName={"annualReport.informationChange.officer.addingOfficers"}
                                fieldLabel={`Are you adding any Officers?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />
                            {values.annualReport.informationChange.officer.addingOfficers === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.officer.addedOfficers"}
                                    values={values.annualReport.informationChange.officer.addedOfficers}
                                    blankEntryFn={() => ({
                                        firstName: "",
                                        lastName: "",
                                        name: "",
                                        address: "",
                                        address2: "",
                                        city: "",
                                        state: "",
                                        zip: "",
                                        title: "",
                                    })}
                                    fields={MontanaOfficerAddressFields}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}


                            <RadioInputField
                                fieldName={"annualReport.informationChange.officer.changingAddresses"}
                                fieldLabel={`Change address for existing Officers?`}
                                errors={errors}
                                required={true}
                                touched={touched}
                                options={yesOrNoValues}
                            />

                            {values.annualReport.informationChange.officer.changingAddresses === "yes" && (
                                <ArrayFieldInput
                                    fieldName={"annualReport.informationChange.officer.changedAddresses"}
                                    values={values.annualReport.informationChange.officer.changedAddresses}
                                    blankEntryFn={() => ({
                                        firstName: "",
                                        lastName: "",
                                        address: "",
                                        address2: "",
                                        city: "",
                                        state: "",
                                        zip: "",
                                    })}
                                    fields={MontanaAgentAddressFields}
                                    touched={touched}
                                    errors={errors}
                                    removeText={"- Click to remove entry"}
                                    addText={"+ Click to add an entry"}
                                />
                            )}

                        </>
                    )}
                </>
            )}










            <div className="terms-and-conditions-container">
                <MontanaAnnualReportTerms />
                <br /><br />
                {iAgreeText}
                <br /><br />
                {selectedProducts["laborLawPoster"] && (
                    <>
                        {laborLawPosterAgreementText}
                        <br /><br />
                    </>
                )}
                {stateTransactionAcknowledgmentText}
                <br /><br />
                <TermsAndConditionAcceptText />
            </div>

            <CheckboxInputField
                fieldName={'termAndConditionAgree'}
                fieldLabel={`I've read and accept the terms and conditions.`}
                touched={touched}
                errors={errors}
                onChange={(e: any) => {
                    const checkedVal = e.target.checked ? true : false;
                    setFieldValue('annualReport.annualAgree', checkedVal)
                    setFieldValue('annualReport.iAmAuthorized', checkedVal)
                    setFieldValue('iAgree', checkedVal)
                    setTimeout(() => {
                        setFieldValue('termAndConditionAgree', checkedVal)
                    }, 0)

                }}
            />

            <SelectFieldInput
                fieldName={"annualReport.signersCapacity"}
                fieldLabel={"Signer's Capacity"}
                errors={errors}
                touched={touched}
                required={true}
                placeholder={"- Select an option -"}
                options={[
                    { value: "self", label: "Self" },
                    { value: "power of attorney", label: "Power of Attorney" },
                    { value: "attorney in fact", label: "Attorney In Fact" },
                    { value: "personal representative of an estate", label: "Personal Representative of an Estate" },
                    { value: "executor of an estate", label: "Executor of an Estate" }
                ]}
            />

            {values.annualReport.signersCapacity && values.annualReport.signersCapacity === "self" && (
                <>
                    <TextInputField
                        fieldName={"annualReport.electronicSignature"}
                        fieldLabel={"Client Agreement & Signature"}
                        fieldSubLabel={electronicSignatureSublabelGeneral}
                        fieldSubLabelCustomClass={'red-text'}
                        errors={errors}
                        touched={touched}
                        required={true}
                        placeholder={"Electronic Signature"}
                        customClass={"signature-font"}
                    />
                </>
            )}


            {values.annualReport.signersCapacity && values.annualReport.signersCapacity !== "self" && (
                <>
                    <TextInputField
                        fieldName={"annualReport.onBehalfOf"}
                        fieldLabel={"On behalf of"}
                        errors={errors}
                        touched={touched}
                        required={true}
                        placeholder={"Enter the person’s name you are signing for."}
                    // customClass={"signature-font"}
                    />

                    <TextInputField
                        fieldName={"annualReport.electronicSignature"}
                        fieldLabel={"Signature of person filling the application"}
                        fieldSubLabel={electronicSignatureSublabelGeneral}
                        fieldSubLabelCustomClass={'red-text'}
                        errors={errors}
                        touched={touched}
                        required={true}
                        placeholder={"Electronic Signature"}
                        customClass={"signature-font"}
                    />
                </>
            )}

            {values.annualReport.electronicSignature && (
                <TextInputField
                    fieldName={"annualReport.dateSigned"}
                    fieldLabel={"Date Signed"}
                    fieldType={"date"}
                    errors={errors}
                    touched={touched}
                    required={true}
                    disabled={true}
                    placeholder={"MM/DD/YYYY"}
                />
            )}

            <SelectFieldInput
                fieldName={"annualReport.title"}
                fieldLabel={"Position"}
                errors={errors}
                touched={touched}
                required={true}
                disabled={!values.annualReport.typeOfEntity ? true : false}
                placeholder={"- Select Position -"}
                options={values?.annualReport?.typeOfEntity === "Corp, INC, other" ? MontanaAnnualCorporationTitleValues : MontanaAnnualTitleValues}
            />

            <TextInputField
                mask={["(", /[1-9]/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/]}
                fieldName={'annualReport.phoneNumber'}
                fieldLabel={'Phone Number'}
                errors={errors}
                touched={touched}
                required={true}
                placeholder={'Phone Number'}
            />

            <TextInputField
                fieldName={'annualReport.email'}
                fieldLabel={'Email'}
                errors={errors}
                touched={touched}
                required={true}
                placeholder={'Email'}
            />

        </>
    )
}

const MontanaAnnualTitleValues = [
    { value: "manager/member", label: "Manager/Member" },
    { value: "authorized agent", label: "Authorized Agent" },
    { value: "attorney in fact", label: "Attorney In Fact" }
];

const MontanaAnnualCorporationTitleValues = [
    { value: "presiding officer board of directors", label: "Presiding Officer Board of Directors" },
    { value: "president", label: "President" },
    { value: "other officer", label: "Other Officer" },
    { value: "authorized agent", label: "Authorized Agent" },
    { value: "attorney in fact", label: "Attorney In Fact" }
];



const MontanaLLCAgentFields: ArrayEntryFieldType[] = [
    {
        fieldName: "entityType",
        fieldLabel: "Entity Type",
        fieldType: "select",
        options: [
            { label: "Individual", value: "individual" },
            { label: "Registered Business Entity", value: "registered business entity" },
            { label: "Non-Registered Organization", value: "non-registered organization" }
        ],
        required: true,
    },
    {
        fieldName: "firstName",
        fieldLabel: "First Name",
        placeholder: "First Name",
        fieldType: "text",
        required: true,
        showIf: {
            entityType: (v: any) => v === 'individual'
        }
    },
    {
        fieldName: "lastName",
        fieldLabel: "Last Name",
        placeholder: "Last Name",
        fieldType: "text",
        required: true,
        showIf: {
            entityType: (v: any) => v === 'individual'
        }
    },

    {
        fieldName: "name",
        fieldLabel: "Registered Business Entity",
        placeholder: "Registered Business Entity",
        fieldType: "text",
        required: true,
        showIf: {
            entityType: (v: any) => v === 'registered business entity'
        }
    },

    {
        fieldName: "name",
        fieldLabel: "Non-Registered Organization",
        placeholder: "Non-Registered Organization",
        fieldType: "text",
        required: true,
        showIf: {
            entityType: (v: any) => v === 'non-registered organization'
        }
    },

    ...(standardAddressFields.map((f: any) => ({
        ...f,
        showIf: {
            entityType: (v: any) => v
        }
    }))),

]

const MontanaLLCAgentAddressFields: ArrayEntryFieldType[] = [

    {
        fieldName: "name",
        fieldLabel: "Name",
        placeholder: "Name",
        fieldType: "text",
        required: true,
    },

    ...(standardAddressFields)
]

const MontanaAgentAddressFields: ArrayEntryFieldType[] = [

    {
        fieldName: "firstName",
        fieldLabel: "First Name",
        placeholder: "First Name",
        fieldType: "text",
        required: true,
    },

    {
        fieldName: "lastName",
        fieldLabel: "Last Name",
        placeholder: "Last Name",
        fieldType: "text",
        required: true,
    },

    ...(standardAddressFields)
]

const MontanaOfficerAddressFields: ArrayEntryFieldType[] = [

    {
        fieldName: "firstName",
        fieldLabel: "First Name",
        placeholder: "First Name",
        fieldType: "text",
        required: true,
    },

    {
        fieldName: "lastName",
        fieldLabel: "Last Name",
        placeholder: "Last Name",
        fieldType: "text",
        required: true,
    },

    ...(standardAddressFields),
    {
        fieldName: "title",
        fieldLabel: "Position",
        fieldType: "select",
        options: [
            { value: "president", label: "President" },
            { value: "vice-president", label: "Vice-President" },
            { value: "treasurer", label: "Treasurer" },
            { value: "secretary", label: "Secretary" },
            { value: "presiding officer", label: "Presiding Officer" },
            { value: "other officer", label: "Other Officer" },
        ],
        required: true,
    }
]